import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ATHENS_GRAY } from "@/shared/colors";
import { ErrorModal, HelmetDexteel, getMomentTz } from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { Moment } from "moment-timezone";
import { useState } from "react";
import { LengthPiecesTable } from "./components/LengthPiecesTable";
import { type BarStatusFilter, Filters } from "./components/filters/Filters";
import { useAllPieces } from "./hooks/useAllPieces";

const moment = getMomentTz();

export const PiecesLengthReportPage = () => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const [filterStartDate, setFilterStartDate] = useState<Moment>(
		moment().subtract(7, "days").startOf("day"),
	);
	const [filterEndDate, setFilterEndDate] = useState<Moment>(
		moment().endOf("day"),
	);
	const [filterProductCode, setFilterProductCode] = useState<number | null>(
		null,
	);
	const [barStatusFilter, setBarStatusFilter] = useState<BarStatusFilter>({
		good: true,
		tooLong: true,
		tooShort: true,
	});

	const [piecesGridApi, setPiecesGridApi] = useState<GridApi | null>(null);

	const { data: allPieces, isLoading: isAllPiecesLoading } = useAllPieces({
		startDate: filterStartDate.toDate(),
		endDate: filterEndDate.toDate(),
		productCode: filterProductCode,
		onError: (e) => setError(e.message),
	});

	const filteredPieces = (allPieces || []).filter((r) => {
		if (r.IsTooLong) return barStatusFilter.tooLong;
		if (r.IsTooShort) return barStatusFilter.tooShort;
		return barStatusFilter.good;
	});

	const handleFilterChange = (startDate: Moment, endDate: Moment) => {
		setFilterStartDate(startDate);
		setFilterEndDate(endDate);
	};

	const handleExportToExcel = () => {
		if (piecesGridApi) {
			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: "pieces_length_report.xlsx",
			};
			piecesGridApi.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	const hasExportData = allPieces && allPieces.length > 0;

	return (
		<>
			<HelmetDexteel title="Pieces Length Report" />
			<Box
				sx={{
					height: "calc(100vh - 64px)",
					display: "flex",
					flexDirection: "column",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				{/* Header */}
				<Box sx={{ px: 2, pt: 2, pb: 1 }}>
					<Grid2 container spacing={1}>
						<Grid2
							size={{ xs: 12 }}
							sx={{ display: "flex", alignItems: "center", gap: 2 }}
						>
							<Typography variant="h5" fontWeight={600}>
								Pieces Length Report
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: "auto" }}>
							<Paper
								elevation={1}
								sx={{
									padding: 2,
									minWidth: 900,
									height: "100%",
									display: "flex",
									alignItems: "center",
								}}
							>
								<Filters
									onFilterChange={handleFilterChange}
									initialStartDate={filterStartDate}
									initialEndDate={filterEndDate}
									productCode={filterProductCode}
									onProductCodeChange={setFilterProductCode}
									barStatusFilter={barStatusFilter}
									onBarStatusFilterChange={setBarStatusFilter}
									showShiftFilter={false}
								/>
							</Paper>
						</Grid2>
						<Grid2 size={{ xs: "grow" }} />
						<Grid2
							size={{ xs: "auto" }}
							sx={{ display: "flex", alignItems: "center" }}
						>
							<ExcelButton
								handleExportToExcel={handleExportToExcel}
								disabled={!hasExportData}
							/>
						</Grid2>
					</Grid2>
				</Box>

				{/* Content */}
				<Box sx={{ flex: 1, overflow: "hidden", px: 2, pb: 2 }}>
					<LengthPiecesTable
						isLoading={isAllPiecesLoading}
						rows={filteredPieces}
						setGridApi={setPiecesGridApi}
					/>
				</Box>
			</Box>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
